import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { Reveal, Overline } from "@/components/Reveal";
import { ArrowLeft, ArrowRight, Check } from "@phosphor-icons/react";
import { SERVICES_DATA } from "@/data/servicesData";
import { useSeo } from "@/lib/useSeo";

const EASE = [0.16, 1, 0.3, 1];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const s = SERVICES_DATA[slug];
  useSeo(
    s ? `${s.title} ${s.accent} — ClaimSpace LegalTech` : "ClaimSpace LegalTech",
    s ? s.seoDescription : ""
  );

  if (!s) return <Navigate to="/" replace />;

  const others = Object.entries(SERVICES_DATA).filter(([k]) => k !== slug);

  return (
    <div data-testid="service-detail-page" className="bg-navy-950 min-h-screen">
      <div className="noise-overlay" />
      <Navbar />

      <header className="pt-40 pb-20 max-w-7xl mx-auto px-6 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
          <Link to="/" data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-mist hover:text-gold transition-colors mb-10">
            <ArrowLeft size={14} /> All practice areas
          </Link>
          <Overline>Practice Area {s.number}</Overline>
          <h1 className="font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-7xl">
            {s.title} <span className="italic text-gold">{s.accent}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-mist text-base sm:text-lg leading-relaxed">{s.intro}</p>
          <div className="flex flex-wrap gap-2 mt-8">
            {s.chips.map((c) => (
              <span key={c} className="text-[11px] uppercase tracking-wider border border-gold/30 text-gold/90 px-3 py-1">{c}</span>
            ))}
          </div>
        </motion.div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 pb-24 flex flex-col gap-20">
        {s.groups.map((g, gi) => (
          <Reveal key={g.title}>
            <div data-testid={`service-group-${gi}`} className="grid lg:grid-cols-12 gap-8 border-t border-gold/20 pt-10">
              <div className="lg:col-span-4 flex gap-6">
                <span className="font-serif text-5xl text-outline-gold leading-none">{String(gi + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-2xl sm:text-3xl text-white">{g.title}</h2>
              </div>
              <ul className="lg:col-span-8 flex flex-col gap-5">
                {g.items.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <Check size={18} weight="bold" className="text-gold mt-1 shrink-0" />
                    <span className="text-mist leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}

        <Reveal>
          <div data-testid="service-cta" className="bg-gold text-navy-950 p-10 sm:p-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-navy-800 mb-3">ClaimSpace LegalTech</p>
              <h2 className="font-serif text-3xl sm:text-4xl">Ready to protect your {s.ctaNoun}?</h2>
            </div>
            <Link to="/#contact" data-testid="service-cta-button"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-navy-950 text-gold font-semibold text-sm tracking-wide hover:bg-navy-900 transition-colors duration-300 shrink-0">
              Request a Quote
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <Reveal>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold font-semibold mb-6">Explore other practice areas</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map(([k, o]) => (
                <Link key={k} to={`/services/${k}`} data-testid={`service-related-${k}`}
                  className="group border border-white/10 hover:border-gold/50 p-5 flex items-center justify-between transition-colors duration-300">
                  <span className="text-white font-serif text-lg">{o.title} {o.accent}</span>
                  <ArrowRight size={16} className="text-gold/40 group-hover:text-gold group-hover:translate-x-1 transition-all duration-300" />
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
